import { FormProvider, observer, useForm } from "@formily/react";
import { useFormily } from "../../hooks/useFormily";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../core/basic/Button";
import type { Form, IFormProps } from "@formily/core";
import type React from "react";
import { getModifiedTopLevelValues, scopedBatch } from "../../utils/formily";
import { useFormSubmitErrorHandler } from "../../hooks/useFormSubmitErrorHandler";
import { HStack } from "../../../styled-system/jsx";

interface Props {
  children: React.ReactNode;
  formOptions?: IFormProps;
}

const IsEditableContext = createContext(false);
export const useIsInEditableWrapper = () => useContext(IsEditableContext);

export function EditableTableWrapper({ children, formOptions = {} }: Props) {
  const form = useFormily(formOptions);
  return (
    <FormProvider form={form}>
      <IsEditableContext.Provider value={true}>{children}</IsEditableContext.Provider>
    </FormProvider>
  );
}

type SubmitButtonProps<TEditableRowData> = {
  onSubmit: (
    values: Record<string, TEditableRowData>,
    form: Form<Record<string, TEditableRowData>>,
  ) => void | Promise<void>;
};

type SubmitAreaViewProps = {
  form: Form;
  cancelLabel: string;
  saveLabel: string;
  onCancel: () => void;
  onSave: () => void;
};

/**
 * Hook-free on purpose: formily's tracker can skip the observed function and reuse its previous
 * result, which drops any hooks inside it and trips React's "Rendered fewer hooks than expected".
 */
const SubmitAreaView = observer(({ form, cancelLabel, saveLabel, onCancel, onSave }: SubmitAreaViewProps) =>
  form.modified ?
    <HStack gap="medium">
      <Button variant="secondary" onPress={onCancel}>
        {cancelLabel}
      </Button>
      <Button isPending={form.submitting} isDisabled={form.invalid} onPress={onSave}>
        {saveLabel}
      </Button>
    </HStack>
  : null,
);

export function EditableTableSubmitArea<TEditableRowData>({ onSubmit }: SubmitButtonProps<TEditableRowData>) {
  const { t } = useTranslation(["common"]);
  const handleSubmitError = useFormSubmitErrorHandler();
  const form = useForm<Record<string, TEditableRowData>>();
  return (
    <SubmitAreaView
      form={form}
      cancelLabel={t(($) => $.common["Cancel Changes"])}
      saveLabel={t(($) => $.common["Save Changes"])}
      onCancel={() => scopedBatch(() => void form.reset())}
      onSave={() => {
        form
          .submit(async (values) => {
            const modifiedValues = getModifiedTopLevelValues(values, form);
            await onSubmit(modifiedValues, form);
          })
          .catch(handleSubmitError);
      }}
    />
  );
}
